import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from './AuthContext';

import { getApiBaseUrl, getApiOrigin } from '../config/apiUrl';

const API_URL = getApiBaseUrl();

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error('Bildirimler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load notifications only for authenticated users
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    fetchNotifications();

    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);

    return () => clearInterval(interval);
  }, [authLoading, user, fetchNotifications]);

  useEffect(() => {
    let eventSource = null;
    let lastToken = null;

    const connectIfNeeded = () => {
      const token = localStorage.getItem('token');
      const isPublicPage = window.location.pathname.startsWith('/public/');

      if (!token || !user || isPublicPage) {
        if (eventSource) {
          eventSource.close();
          eventSource = null;
        }
        return;
      }

      if (eventSource && lastToken && lastToken !== token) {
        eventSource.close();
        eventSource = null;
      }

      if (!eventSource) {
        lastToken = token;
        eventSource = new EventSource(`${API_URL}/events?token=${encodeURIComponent(token)}`);

        eventSource.addEventListener('notification', (event) => {
          const payload = JSON.parse(event.data);
          const notification = payload.notification || payload;
          setNotifications(prev => [notification, ...prev]);
          if (!notification.read) {
            setUnreadCount(prev => prev + 1);
          }
          const message = notification.message || notification.title || 'Yeni bildirim';
          switch (notification.type) {
            case 'quote_accepted':
              toast.success(message);
              break;
            case 'quote_rejected':
            case 'error':
              toast.error(message);
              break;
            case 'warning':
              toast(message, { icon: '⚠️' });
              break;
            default:
              toast(message);
          }
        });

        eventSource.addEventListener('quote_status', (event) => {
          const payload = JSON.parse(event.data);
          window.dispatchEvent(new CustomEvent('quote-status', { detail: payload }));
        });

        eventSource.addEventListener('order_created', (event) => {
          const payload = JSON.parse(event.data);
          window.dispatchEvent(new CustomEvent('order-created', { detail: payload }));
        });
      }
    };

    const interval = setInterval(connectIfNeeded, 1000);
    connectIfNeeded();

    return () => {
      clearInterval(interval);
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [user]);

  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now(),
      read: false,
      createdAt: new Date(),
      ...notification,
    };
    setNotifications(prev => [newNotification, ...prev]);
  }, []);

  const markAsRead = useCallback(async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => (n._id === id ? { ...n, read: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Bildirim okundu işaretlenirken hata:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Tüm bildirimler okundu işaretlenirken hata:', error);
    }
  }, []);

  const deleteNotification = useCallback(async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n._id !== id));
      // Recalculate unread count
      setUnreadCount(prev => {
        const notification = notifications.find(n => n._id === id);
        return notification && !notification.read ? Math.max(0, prev - 1) : prev;
      });
    } catch (error) {
      console.error('Bildirim silinirken hata:', error);
    }
  }, [notifications]);

  const clearAll = useCallback(async () => {
    try {
      await api.delete('/notifications');
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Bildirimler temizlenirken hata:', error);
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    fetchNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
